import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { WindowRefService } from '../window-ref.service';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { CartService } from '../../../core/services/cart.service';

export interface CartItem {
  id: string;
  itemId: number;
  name: string;
  price: number;
  quantity: number;
  imageUrl: string;
  options?: any[];
  extras?: {
    group: string;
    choice: string;
    price: number;
  }[];
}

@Component({
  selector: 'app-cart',
  standalone: false,
  templateUrl: './cart.component.html',
  styleUrl: './cart.component.scss',
})
export class CartComponent implements OnInit {
  cartItems: CartItem[] = [];
  showFeesModal = false;

  @Input() deliveryFeeOriginalGross: number = 0;
  @Input() serviceFee: number = 0;
  @Input() minimumDeliveryAmount: number = 0;
  @Input() minimumOrderAmount: number = 0;
  @Input() smallOrderFee: number = 0;
  @Input() orderType: 'delivery' | 'collection' = 'delivery';

  @Input() deliveryDisabled: boolean = false;
  @Input() deliveryToggleLabel: string = '';

  @Output() checkout = new EventEmitter<void>();
  @Output() orderTypeChange = new EventEmitter<'delivery' | 'collection'>();

  isMobile = false;
  mobileCartOpen = false;

  initialized = false;

  orderNotes: string = '';
  showNotesModal = false;

  private readonly ORDER_NOTES_KEY = 'waitron.orderNotes';

  constructor(
    private cartService: CartService,
    private windowRef: WindowRefService,
    private analytics: AnalyticsService,
  ) {
    this.cartService.cart$.subscribe((items) => {
      this.cartItems = items;
    });
  }

  ngOnInit(): void {
    this.checkMobile();

    const savedNotes = sessionStorage.getItem(this.ORDER_NOTES_KEY);
    if (savedNotes) this.orderNotes = savedNotes;

    setTimeout(() => {
      this.initialized = true;
    }, 50);
  }

  checkMobile(): void {
    const win = this.windowRef.nativeWindow;
    this.isMobile = win && win.innerWidth <= 768;
    if (!this.isMobile) this.mobileCartOpen = false;
  }

  toggleMobileCart(): void {
    this.mobileCartOpen = !this.mobileCartOpen;
    document.body.style.overflow = this.mobileCartOpen ? 'hidden' : '';

    if (this.mobileCartOpen) {
      this.analytics.event('view_cart', {
        items: this.mapCartItems(),
        currency: 'GBP',
        value: this.getSubtotal(),
        order_type: this.orderType,
      });

      setTimeout(() => {
        const cart = document.querySelector(
          '.cart-container.mobile-overlay',
        ) as HTMLElement;
        if (cart) {
          cart.style.maxHeight = `${window.innerHeight * 0.9}px`;
        }
      }, 100);
    }
  }

  changeOrderType(type: 'delivery' | 'collection'): void {
    if (type === this.orderType) return;
    if (type === 'delivery' && this.deliveryDisabled) {
      this.orderTypeChange.emit('delivery');
      return;
    }

    this.orderTypeChange.emit(type);
  }

  updateQuantity(item: CartItem, change: number): void {
    const newQty = item.quantity + change;

    this.cartService.updateQuantity(item.id, newQty);

    if (change > 0) {
      this.analytics.event('add_to_cart', {
        items: [this.mapSingleItem(item, 1)],
        currency: 'GBP',
        value: item.price,
        order_type: this.orderType,
      });
    }

    if (change < 0) {
      this.analytics.event('remove_from_cart', {
        items: [this.mapSingleItem(item, 1)],
        currency: 'GBP',
        value: item.price,
        order_type: this.orderType,
      });
    }
  }

  removeItem(itemId: string): void {
    const item = this.cartItems.find((i) => i.id === itemId);
    if (!item) return;

    this.analytics.event('remove_from_cart', {
      items: [this.mapSingleItem(item, item.quantity)],
      currency: 'GBP',
      value: item.price * item.quantity,
      order_type: this.orderType,
    });

    this.cartService.removeItem(itemId);
  }

  getSubtotal(): number {
    return this.cartService.getTotalPrice();
  }

  getTotal(): number {
    const subtotal = this.getSubtotal();
    const delivery = this.isDelivery ? this.deliveryFeeOriginalGross : 0;
    const smallOrder = this.getSmallOrderFee();
    return subtotal + delivery + this.serviceFee + smallOrder;
  }

  getSmallOrderFee(): number {
    return this.getSubtotal() < this.minimumOrderAmount
      ? this.smallOrderFee
      : 0;
  }

  proceedToCheckout(): void {
    if (!this.canProceedToCheckout) return;

    if (this.cartItems.length > 0) {
      this.analytics.event('begin_checkout', {
        items: this.mapCartItems(),
        currency: 'GBP',
        value: this.getTotal(),
        order_type: this.orderType,
      });
    }

    this.mobileCartOpen = false;
    document.body.style.overflow = '';
    this.checkout.emit();
  }

  get totalItemCount(): number {
    return this.cartItems.reduce((sum, item) => sum + item.quantity, 0);
  }

  openFeesModal() {
    this.showFeesModal = true;
  }

  closeFeesModal() {
    this.showFeesModal = false;
  }

  /** explain in tooltip why each fee exists */
  getFeesExplanationLines(): string[] {
    return [
      `Delivery fee covers our riders’ costs.`,
      `Service fee helps us maintain the platform.`,
      `Small-order fee applies when your basket subtotal is below £${this.minimumOrderAmount.toFixed(
        2,
      )}.`,
    ];
  }

  openNotesModal() {
    this.showNotesModal = true;
  }

  closeNotesModal() {
    this.showNotesModal = false;
  }

  saveNotes() {
    const trimmed = (this.orderNotes || '').trim();

    if (trimmed) {
      this.analytics.event('add_order_note', {
        message: trimmed,
        order_type: this.orderType,
      });

      sessionStorage.setItem(this.ORDER_NOTES_KEY, trimmed);
    } else {
      sessionStorage.removeItem(this.ORDER_NOTES_KEY);
    }

    this.closeNotesModal();
  }

  get isDelivery(): boolean {
    return this.orderType === 'delivery';
  }

  private mapCartItems() {
    return this.cartItems.map((item) => ({
      item_id: item.id,
      item_name: item.name,
      price: item.price,
      quantity: item.quantity,
      item_variant: item.extras?.map((e) => e.choice).join(', ') || undefined,
    }));
  }

  private mapSingleItem(item: CartItem, quantity: number) {
    return {
      item_id: item.id,
      item_name: item.name,
      price: item.price,
      quantity,
      item_variant: item.extras?.map((e) => e.choice).join(', ') || undefined,
    };
  }

  get minimumGuardTotal(): number {
    const subtotal = this.getSubtotal();
    const smallOrderFee = this.getSmallOrderFee();

    // Delivery fee and service fee do not count towards minimum guard logic
    return subtotal + smallOrderFee;
  }

  get canProceedToCheckout(): boolean {
    return this.cartItems.length > 0 && !this.isBelowMinimum;
  }

  get activeMinimumAmount(): number {
    return this.isDelivery
      ? this.minimumDeliveryAmount
      : this.minimumOrderAmount;
  }

  get isBelowMinimum(): boolean {
    if (this.cartItems.length === 0) return false;

    if (!this.isDelivery) return false;

    return this.minimumGuardTotal < this.minimumDeliveryAmount;
  }

  get amountRemainingToMinimum(): number {
    if (!this.isDelivery) return 0;

    return Math.max(0, this.minimumDeliveryAmount - this.minimumGuardTotal);
  }

  get minimumOrderMessage(): string {
    if (!this.isDelivery) return '';

    const remaining = this.amountRemainingToMinimum;
    if (remaining <= 0) return '';

    return `Add ${new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(
      remaining,
    )} more to reach the minimum delivery amount. Small-order fee counts towards this minimum, but delivery and service fees do not.`;
  }
}
